<template>
  <b-overlay :show="loading" class="mt-1">
    <b-alert v-if="state === 'unavailable'" show variant="warning" class="p-1">
      Electronic file temporarily unavailable.
    </b-alert>
    <b-alert v-else-if="state === 'denied'" show variant="info" class="p-1">
      You do not have access to any documents in this file.
    </b-alert>
    <b-alert v-else-if="state === 'none'" show variant="info" class="p-1">
      No electronic file has been opened for this person yet.
    </b-alert>

    <template v-else-if="state === 'ready'">
      <b-row class="mb-1">
        <b-col md="3"><small class="text-muted d-block">File number</small>{{ summary.file_number || "-" }}</b-col>
        <b-col md="3"><small class="text-muted d-block">Custodian</small>{{ summary.custodian || "-" }}</b-col>
        <b-col md="2">
          <small class="text-muted d-block">Status</small>
          <b-badge variant="light-primary" class="text-capitalize">{{ summary.status || "-" }}</b-badge>
        </b-col>
        <b-col md="2"><small class="text-muted d-block">Completeness</small>{{ summary.completeness_pct != null ? summary.completeness_pct + "%" : "-" }}</b-col>
        <b-col md="2"><small class="text-muted d-block">Last added</small>{{ formatDate(summary.last_added_at) }}</b-col>
      </b-row>

      <b-row class="mb-1">
        <b-col md="4">
          <v-select
            v-model="category"
            :options="categories"
            placeholder="All categories"
            :clearable="true"
            @input="reload"
          />
        </b-col>
      </b-row>

      <b-table
        responsive
        hover
        show-empty
        empty-text="No documents match."
        :items="documents"
        :fields="fields"
      >
        <template #cell(confidentiality)="data">
          <b-badge :variant="data.value === 'standard' ? 'light-secondary' : 'light-danger'">{{ data.value }}</b-badge>
        </template>
        <template #cell(document_date)="data">{{ formatDate(data.value) }}</template>
        <template #cell(added_at)="data">{{ formatDate(data.value) }}</template>
        <template #cell(actions)="data">
          <b-button v-if="can('member_efile:CanView')" size="sm" variant="outline-primary" class="mr-50" @click="open(data.item, false)">View</b-button>
          <b-button v-if="can('member_efile:CanDownload')" size="sm" variant="outline-secondary" @click="open(data.item, true)">Download</b-button>
        </template>
      </b-table>

      <b-button v-if="nextCursor" variant="flat-primary" size="sm" @click="loadMore">Load more</b-button>

      <div v-if="can('member_efile:CanUpload')" class="mt-2">
        <h6>Add a document</h6>
        <b-row>
          <b-col md="4">
            <v-select v-model="uploadCategory" :options="categories" placeholder="Category" />
          </b-col>
          <b-col md="5">
            <input ref="file" type="file" class="form-control" @change="onFile">
          </b-col>
          <b-col md="3">
            <b-button variant="primary" :disabled="!uploadCategory || !pendingFile || uploading" @click="upload">
              {{ uploading ? "Uploading..." : "Upload" }}
            </b-button>
          </b-col>
        </b-row>
        <small class="text-muted">Documents go to the file service for scanning and review and appear once filed.</small>
      </div>
    </template>
  </b-overlay>
</template>

<script>
import vSelect from "vue-select";
import { get } from "lodash";
import { BAlert, BBadge, BButton, BCol, BOverlay, BRow, BTable } from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

// Tab body for P16. All data comes from PAC, which proxies the file provider. Nothing here is
// persisted in the browser beyond this component's state.
export default {
  components: { BAlert, BBadge, BButton, BCol, BOverlay, BRow, BTable, vSelect },
  props: {
    profileId: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      // ready | none | denied | unavailable
      state: "unavailable",
      summary: {},
      documents: [],
      nextCursor: null,
      category: null,
      categories: [],
      uploadCategory: null,
      pendingFile: null,
      uploading: false,
      fields: [
        { key: "title", label: "Title" },
        { key: "category", label: "Category" },
        { key: "document_date", label: "Document date" },
        { key: "version", label: "Version" },
        { key: "confidentiality", label: "Confidentiality" },
        { key: "source", label: "Source" },
        { key: "added_at", label: "Added" },
        { key: "actions", label: "" },
      ],
    };
  },
  created() {
    this.init();
  },
  methods: {
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString() : "-";
    },
    service() {
      return this.useJwt().mainService.memberEfile;
    },
    async init() {
      this.loading = true;
      try {
        const { data } = await this.service().summary(this.profileId);
        const summary = data.data || data;
        if (!summary.exists) {
          this.state = "none";
          return;
        }
        this.summary = summary;
        this.categories = Object.keys(summary.counts_by_category || {});
        await this.loadDocuments(false);
      } catch (error) {
        this.state = get(error, "response.status") === 403 ? "denied" : "unavailable";
      } finally {
        this.loading = false;
      }
    },
    async loadDocuments(append) {
      const params = { limit: 25 };
      if (this.category) params.category = this.category;
      if (append && this.nextCursor) params.cursor = this.nextCursor;
      const { data } = await this.service().documents(this.profileId, params);
      const rows = data.data || [];
      this.documents = append ? this.documents.concat(rows) : rows;
      this.nextCursor = get(data, "pagination.next_cursor") || null;
      this.state = this.documents.length || this.category ? "ready" : "denied";
    },
    async reload() {
      this.loading = true;
      try {
        await this.loadDocuments(false);
      } catch (error) {
        this.state = "unavailable";
      } finally {
        this.loading = false;
      }
    },
    async loadMore() {
      try {
        await this.loadDocuments(true);
      } catch (error) {
        this.toastError("Could not load more documents.");
      }
    },
    async open(doc, download) {
      // Open the window synchronously so the browser does not block it as a popup
      const win = download ? null : window.open("", "_blank");
      try {
        const { data } = await this.service().link(this.profileId, doc.id, { download });
        const url = get(data, "data.url") || data.url;
        if (win) {
          win.location = url;
        } else {
          window.location.assign(url);
        }
      } catch (error) {
        if (win) win.close();
        this.toastError(get(error, "response.data.message") || "Could not get a link for this document.");
      }
    },
    onFile(event) {
      this.pendingFile = (event.target.files || [])[0] || null;
    },
    // P20: PAC never sees the bytes. It returns an upload slot from the file provider and the browser
    // sends the file straight there.
    async upload() {
      this.uploading = true;
      try {
        const { data } = await this.service().requestUpload(this.profileId, { category: this.uploadCategory });
        const slot = data.data || data;
        const response = await fetch(slot.upload_url, { method: "PUT", body: this.pendingFile });
        if (!response.ok) throw new Error("The file service rejected the file");
        this.$toast({
          component: ToastificationContent,
          props: { title: "Uploaded", icon: "CheckIcon", variant: "success", text: "The document is pending review in the file service." },
        });
        this.pendingFile = null;
        if (this.$refs.file) this.$refs.file.value = "";
      } catch (error) {
        this.toastError(get(error, "response.data.message") || error.message || "Upload failed.");
      } finally {
        this.uploading = false;
      }
    },
    toastError(text) {
      this.$toast({
        component: ToastificationContent,
        props: { title: "Error!", icon: "AlertTriangleIcon", variant: "danger", text },
      });
    },
  },
};
</script>
